import { FC } from 'react';
import cn from 'classnames';
import { Typography } from '@mui/material';
import { translate } from 'localizations';
import { useAppSelector } from 'hooks/redux';
import { CriteriaTemplate, CriteriaWithConditions, Hint } from 'components/common';
import CriteriaItem from '../CriteriaItem';
import { useCriteriaStyles } from '../hooks/useCriteriaStyles';
import TooltipCustom from 'components/common/Tooltip/Tooltip';
import { optionsCreatorSimpleTitle, ValueObj } from 'utils/optionsCreator';
import { IconQuestion, TitleWithTooltip } from 'shared/ui';
import { CriteriasTopHeader } from './CriteriasTopHeader';
import { BLOCK_NUMBER_FIRST, BLOCK_NUMBER_FOURTH, BLOCK_NUMBER_THIRD } from '../consts';
import { Page } from '../types';
import type { CriteriasType } from 'store/search/search.types';
import type { ConvertedTemplateOption, HandlerOnChange, ICriteriaMultiList } from '../types';

const CriteriaMultiList: FC<ICriteriaMultiList> = ({
	activeCriterias,
	allCriterias,
	sortedCriterias,
	block,
	handlerOnChange,
	isHandlerFunction,
	page,
	onAddCondition,
	onDeleteCondition,
	simpleListWithTitles,
	blockTitle,
	titleTooltip,
	onTemplateSelectValueChange,
	convertedTemplate,
	currentTemplate,
	allTemplates,
	onClearTemplateClick,
	hideParametersBlock,
}) => {
	const { language } = useAppSelector((state) => state.lang);
	const { classes, getBlockClassName, getBlockSearchClassName } = useCriteriaStyles();
	const showTopHeader = page !== Page.CallsFromReportCell && page !== Page.Reports && !simpleListWithTitles;
	const showTitleWithTooltip = page === Page.Checklists;
	const itemsClass = simpleListWithTitles ? classes.searchItemsFullWidth : classes.searchItems;

	return (
		<div className={classes.root}>
			{showTopHeader && (
				<CriteriasTopHeader
					classes={classes}
					language={language}
					blockTitle={blockTitle}
					titleTooltip={titleTooltip}
					currentTemplate={currentTemplate}
					convertedTemplate={convertedTemplate}
					allTemplates={allTemplates}
					simpleListWithTitles={simpleListWithTitles}
					onTemplateSelectValueChange={onTemplateSelectValueChange}
					onClearTemplateClick={onClearTemplateClick}
					hideParametersBlock={hideParametersBlock}
				/>
			)}

			{showTitleWithTooltip && (
				<TitleWithTooltip
					title={translate('callFilters', language)}
					tooltipTitle={translate('callFilters', language)}
					theme="black"
					size="mdL"
				/>
			)}
			<div className={itemsClass}>
				{activeCriterias.length > 0 ? (
					allCriterias &&
					sortedCriterias.map((criteriasObj, index) => {
						const isFirstBlock =
							criteriasObj.blockNumber === BLOCK_NUMBER_FIRST && page === Page.CallsFromReportCell;
						const isThirdBlock =
							criteriasObj.blockNumber === BLOCK_NUMBER_THIRD && page === Page.CallsFromReportCell;
						const isSimpleListThirdBlock =
							simpleListWithTitles && criteriasObj.blockNumber === BLOCK_NUMBER_THIRD;
						const isFourthBlock = criteriasObj.blockNumber === BLOCK_NUMBER_FOURTH && page !== Page.Deals;

						const showTemplates =
							currentTemplate !== undefined && allTemplates !== undefined && !simpleListWithTitles;

						return (
							<div
								className={cn(
									getBlockClassName(criteriasObj.blockNumber, simpleListWithTitles || false),
								)}
								key={criteriasObj.blockNumber}
							>
								{isFirstBlock && (
									<div className={classes.searchTitleTop}>
										<div className={classes.searchTitleLeft}>
											<Typography className={classes.searchTitleTitle} variant="subtitle1">
												{blockTitle || translate('fragmentSearch', language)}
											</Typography>
											<TooltipCustom
												background="dark"
												title={translate('tooltipFragments', language)}
												placement="right"
												followCursor={false}
											>
												<div className={classes.questionWrapper}>
													<IconQuestion />
													<div className={classes.questionTitle}>
														{translate('howToSearch', language)}
													</div>
												</div>
											</TooltipCustom>
											{showTemplates && (
												<CriteriaTemplate
													height="400px"
													name="templatesCalls"
													title={translate('savedSearchTemp', language)}
													handleValueChange={
														onTemplateSelectValueChange as (value: ValueObj) => void
													}
													options={convertedTemplate as ConvertedTemplateOption[]}
													currentTemplate={optionsCreatorSimpleTitle(currentTemplate)}
													allTemplates={allTemplates}
													onClearTemplateClick={onClearTemplateClick}
													dataTestTd="templatesCalls"
												/>
											)}
										</div>
									</div>
								)}
								{!simpleListWithTitles && isThirdBlock && (
									<div className={classes.searchTitleTop}>
										<div className={classes.searchTitleLeft}>
											<Typography className={classes.searchTitleTitle} variant="subtitle1">
												{translate('callsParameters', language)}
											</Typography>
											<TooltipCustom
												background="dark"
												title={translate('tooltipParam', language)}
												placement="bottom"
												followCursor={false}
											>
												<div className={classes.questionWrapper}>
													<IconQuestion />
													<div className={classes.questionTitle}>
														{translate('whatIsIt', language)}
													</div>
												</div>
											</TooltipCustom>
										</div>
									</div>
								)}
								{isSimpleListThirdBlock && (
									<div className={classes.searchTitle}>
										<div className={classes.searchTitleLeft}>
											<Typography className={classes.searchTitleTitle} variant="subtitle1">
												{translate('callsParameters', language)}
											</Typography>
											<Hint
												title={translate('tooltipParam', language)}
												label={translate('whatIsIt', language)}
											/>
										</div>
									</div>
								)}
								{isFourthBlock && (
									<div className={classes.searchTitle}>
										<div className={classes.searchTitleLeft}>
											<Typography className={classes.searchTitleTitle} variant="subtitle1">
												{translate('reportAddParameters', language)}
											</Typography>
											<Hint
												title={translate('tooltipTags', language)}
												label={translate('whatIsIt', language)}
												placement="right"
											/>
										</div>
									</div>
								)}
								<div className={classes.criteriaRow}>
									{criteriasObj.criterias.map((criteria: CriteriasType) => {
										const compResult = allCriterias.filter(
											(v: { key: string }) => v.key === criteria.key,
										);
										return (
											<div
												className={cn(getBlockSearchClassName(criteria.block))}
												key={criteria.key}
											>
												<CriteriaItem
													title={criteria.title}
													criteriaFull={compResult[0]}
													criteriaCurrent={criteria}
													block={block as Page}
													handlerOnChange={handlerOnChange as HandlerOnChange}
													isHandlerFunction={isHandlerFunction}
													page={page}
												/>
											</div>
										);
									})}
									{Object.keys(criteriasObj.complexCriterias).length > 0 &&
										Object.keys(criteriasObj.complexCriterias).map((item) => (
											<CriteriaWithConditions
												key={criteriasObj.complexCriterias[item][0].key}
												criteriaKey={criteriasObj.complexCriterias[item][0].key}
												allCriterias={allCriterias}
												data={criteriasObj.complexCriterias[item]}
												block={block as Page}
												index={index}
												handlerOnChange={handlerOnChange as HandlerOnChange}
												isHandlerFunction={isHandlerFunction}
												page={page}
												onAddCondition={onAddCondition}
												onDeleteCondition={onDeleteCondition}
											/>
										))}
								</div>
							</div>
						);
					})
				) : (
					<div>
						<Typography className={classes.emptyText}>{translate('searchEmpty', language)}</Typography>
					</div>
				)}
			</div>
		</div>
	);
};

export default CriteriaMultiList;
